import { apiClient } from '@/lib/apiClient';

export interface DashboardStats {
  totalUsers: number;
  activeUsers24h: number;
  activeTeams: number;
  activeGoals: number;
  todayCheckins: number;
  goalsByStatus: { draft: number; active: number; graduated: number; total: number };
  teamsByStatus: Record<string, number>;
}

export interface ActivityItem {
  type: 'checkin';
  userId: string;
  email?: string;
  displayName?: string;
  avatarUrl?: string;
  timestamp: string;
  details: {
    checkinId: string;
    playMode: 'solo' | 'team';
    teamId?: string;
    mood?: string;
    awardedPoints: number;
  };
}

export interface ChartPoint {
  date: string;
  count: number;
}

export type ExperimentHealth = 'new' | 'on_track' | 'slipping' | 'silent';

export interface FunnelCounts {
  signedUp: number;
  startedExperiment: number;
  loggedOnce: number;
  cameBack: number;
  logged4Days: number;
  secondExperiment: number;
}

export interface PivotDashboard {
  generatedAt: string;
  pivotStart: string;
  excludedUsers: number;
  kpis: {
    loggedToday: number;
    weeklyLoggers: number;
    liveExperiments: number;
    signups7d: number;
  };
  reminder: { allowed: boolean; sent7d: number; users7d: number };
  funnel: FunnelCounts;
  cohorts: (FunnelCounts & { weekStart: string })[];
  dailyLoggers: { date: string; done: number; missed: number }[];
  liveExperiments: {
    phaseId: string;
    userId: string;
    displayName?: string;
    email?: string;
    objective: string;
    phaseNumber: number;
    day: number;
    totalDays: number;
    daysLogged: number;
    loggedToday: boolean;
    lastLogAt: string | null;
    health: ExperimentHealth;
  }[];
  awaitingWrap: {
    phaseId: string;
    userId: string;
    displayName?: string;
    email?: string;
    objective: string;
    endDate: string;
    daysWaiting: number;
    daysLogged: number;
  }[];
  staleEvaluating: number;
}

export const dashboardApi = {
  pivot: () => apiClient.get<PivotDashboard>('/dashboard/pivot'),

  stats: () => apiClient.get<DashboardStats>('/dashboard/stats'),

  activity: (limit = 15) =>
    apiClient.get<{ items: ActivityItem[] }>('/dashboard/activity', { limit }),

  userGrowth: (days = 30) =>
    apiClient.get<{ series: ChartPoint[] }>('/dashboard/charts/user-growth', { days }),

  checkinRate: (days = 30) =>
    apiClient.get<{ series: ChartPoint[] }>('/dashboard/charts/checkin-rate', { days }),

  goalCompletion: (days = 30) =>
    apiClient.get<{ series: ChartPoint[] }>('/dashboard/charts/goal-completion', { days }),
};
